import { useState } from "react";
import { Alert, Autocomplete, Stack, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import {
  type BookableAppointmentSubject,
  useBookingSubjects,
} from "@/components/appointmentRequest/useBookingSubjects";

interface Props {
  requestId: string;
  hasSelectedSlot: boolean;
  submitting: boolean;
  onConfirm: (subject: BookableAppointmentSubject) => void;
}

export default function WorkerSlotAssignmentForm(props: Props) {
  const { t } = useTranslation();
  const [selectedSubject, setSelectedSubject] =
    useState<BookableAppointmentSubject | null>(null);
  const {
    subjects,
    search,
    loading,
    failed,
    hasMore,
    changeSearch,
    loadMore,
    retry,
  } = useBookingSubjects(props.hasSelectedSlot, props.requestId);
  const noWorkersRemain =
    props.hasSelectedSlot &&
    !loading &&
    !failed &&
    !search &&
    subjects.length === 0;
  const canSubmit =
    Boolean(props.hasSelectedSlot && selectedSubject) &&
    !props.submitting &&
    !loading &&
    !failed &&
    !noWorkersRemain;

  return (
    <Stack
      component="form"
      spacing={2}
      sx={{ mt: 2 }}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit && selectedSubject) props.onConfirm(selectedSubject);
      }}
    >
      <Autocomplete
        options={subjects}
        value={selectedSubject}
        loading={loading}
        disabled={!props.hasSelectedSlot || props.submitting}
        filterOptions={(options) => options}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(subject) =>
          `${subject.name}${subject.is_analog ? ` (${t("analog_user")})` : ""}`
        }
        noOptionsText={t("no_workers_available_for_booking")}
        loadingText={t("loading")}
        onChange={(_event, subject) => setSelectedSubject(subject)}
        onInputChange={(_event, value, reason) => {
          if (reason === "input" || reason === "clear") {
            setSelectedSubject(null);
            changeSearch(value);
          }
        }}
        renderInput={(params) => (
          <TextField {...params} label={t("select_worker_for_appointment")} />
        )}
      />
      {failed && (
        <Alert severity="error">{t("worker_booking_load_failed")}</Alert>
      )}
      {(hasMore || failed) && (
        <GenericButton
          variant="outlined"
          disabled={loading || props.submitting}
          onClick={failed ? retry : loadMore}
        >
          {t(failed ? "retry" : "load_more")}
        </GenericButton>
      )}
      <GenericButton
        type="submit"
        data-testid="confirm-worker-appointment"
        disabled={!canSubmit}
        loading={props.submitting}
        showSpinnerOnDisable
      >
        {t(
          noWorkersRemain
            ? "no_more_worker_assignments"
            : "assign_worker_appointment",
        )}
      </GenericButton>
    </Stack>
  );
}
